import { capacitorAuth } from '../capacitor/auth';
import { User } from '../../types';
import { firebaseAuth } from '../firebase/auth';

const API_URL = import.meta.env.VITE_API_URL;

export interface RegisterResponse {
  success: boolean;
  data?: {
    registerResponse: any;
  }
  error?: string;
}


class AuthAPI {
  private async request(endpoint: string, options: RequestInit = {}) {
    try {
      if (!API_URL) {
        throw new Error('API_URL is not defined in environment variables');
      }

      const idToken = await firebaseAuth.getIdToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        ...options.headers
      };

      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`${response.status} - ${errorText}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please check if the server is running.');
      }
      throw error;
    }
  }

  async login(email: string, password: string): Promise<User> {
    const idToken = await capacitorAuth.signIn(email, password);

    const response = this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ idToken })
    });

    return response
  }

  async register(email: string, password: string, name: string, acceptedTerms: boolean): Promise<RegisterResponse> {

    if (!acceptedTerms) {
      throw new Error('Terms must be accepted');
    }
    const idToken = await capacitorAuth.register(email, password);

    const registerResponse = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ idToken, name, acceptedTerms })
    });

    if (registerResponse.error) {
      return { success: false, error: registerResponse.error };
    }

    const response = {
      success: true,
      data: {
        registerResponse
      }
    }

    return response
  }

  async isEmailVerified(email: string): Promise<boolean> {
    try {
      const response = await this.request("/api/auth/verification-status", {
        method: "POST",
        body: JSON.stringify({ email }),
      });

      return response.isVerified === true;
    } catch (error) {
      console.error("Error checking email verification:", error);
      return false;
    }
  }
  async getMe(): Promise<User> {
    const me = this.request('/api/auth/me');

    if (!me) {
      throw new Error('User not authenticated');
    }

    return me;
  }

  async logout(): Promise<void> {
    await capacitorAuth.signOut();
  }
}

export const authAPI = new AuthAPI();